
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useParams } from "react-router-dom";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import NotFoundPage from "../NotFoundPage/NotFoundPage";
import "./IFrame.scss";
import TextTile from "../TextTile/TextTile";

const mapState = (state: State) => ({ menu: state.menu });
const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & { type: "embed" | "instructions" };

const PROBE_TIMEOUT_MS = 8000; // Timeout voor reachability check

type PrecheckStatus = "checking" | "ok" | "fail";

const IFrame: FC<Props> = ({ menu, type }) => {
  const { path } = useParams();
  const [url, setUrl] = useState<string | null>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [precheckStatus, setPrecheckStatus] = useState<PrecheckStatus>("checking");
  const abortRef = useRef<AbortController | null>(null);

  const findById = useCallback(
    (arr: any[], path: any): MainMenuItemInterface | undefined => {
      if (arr?.length === 0) return;
      return (
        arr.find((d) => d.path === path) ||
        findById(arr.flatMap((d) => d.children || []), path) ||
        null
      );
    },
    []
  );

  useEffect(() => {
    const data = findById(menu, path);
    setUrl(type === "embed" ? data?.url : data?.documentationUrl);
    setTimeout(() => setIsLoading(false), 1000);
  }, [findById, menu, path, type]);

  // Cleanup abort bij unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Reachability check
  useEffect(() => {
    if (!url) return;

    setPrecheckStatus("checking");

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const timeoutId = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

    fetch(url, { method: "GET", mode: "no-cors", signal: controller.signal })
      .then(() => setPrecheckStatus("ok"))
      .catch(() => setPrecheckStatus("fail"))
      .finally(() => {
        window.clearTimeout(timeoutId);
        abortRef.current = null;
      });

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [url]);

  return isLoading ? (
    <LoadingScreen />
  ) : url ? (
    <>
      {precheckStatus === "fail" && (

<TextTile
  title={"Toegang tot lokaal netwerk vereist"}
  body={`
    <p>
      De Kennisbank kan niet worden geladen omdat de verbinding wordt geblokkeerd of niet bereikbaar is.
      Volg onderstaande stappen om dit op te lossen:
    </p>

    <h3>Stap 1: Zet Zscaler aan</h3>
    <p>Als je Zscaler gebruikt, activeer deze. Dit kan het probleem oplossen.</p>

    <h3>Stap 2: Sta lokaal netwerk toe in Edge</h3>
    <ol>
      <li>Klik op het <strong>slotje</strong> links van het webadres in de adresbalk.</li>
      <li>Zet <strong>Local network access</strong> op <em>Allowed</em>.</li>
      <li>
        Staat deze optie niet zichtbaar?
        <ul>
          <li>Klik op <strong>Permissions for this site</strong>.</li>
          <li>Zet <strong>Local network access</strong> daar op <em>Allow</em>.</li>
        </ul>
      </li>
      <li>Herstart de website en/of je browser (soms noodzakelijk).</li>
    </ol>

    <p>
      <strong>Tip:</strong> Als je eerder op <em>Block</em> hebt geklikt, voer bovenstaande stappen uit om toegang opnieuw toe te staan.
    </p>
  `}
/>

      )}

      {precheckStatus === "ok" && (
        <iframe
          src={url}
          title="Embed"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      )}

      {precheckStatus === "checking" && <LoadingScreen />}
    </>
  ) : (
    <NotFoundPage />
  );
};

export default connector(IFrame);
